import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import { useJob, useUpdateJob, useCustomers } from "@/hooks/use-api";
import {
  jobFields,
  jobToFormValues,
  type JobFormValues,
} from "./job-form-config";

export default function V2JobEditPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const jobQuery = useJob(id);
  const updateJob = useUpdateJob();
  const customersQuery = useCustomers({ limit: 1000 });
  const [error, setError] = useState<string | null>(null);

  const customerOptions = (
    (
      customersQuery.data as
        | { customers?: Record<string, unknown>[] }
        | undefined
    )?.customers ?? []
  ).map((c) => ({
    value: String(c.contact_role_id ?? ""),
    label:
      [c.first_name, c.last_name].filter(Boolean).join(" ") ||
      String(c.company_name ?? ""),
  }));

  async function onSubmit(values: JobFormValues) {
    setError(null);
    try {
      await updateJob.mutateAsync({
        id,
        input: values as Record<string, unknown>,
      });
      toast.success("Job updated");
      navigate(`/jobs/${id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your changes — check the fields and try again.",
      );
    }
  }

  const job = (jobQuery.data as { job?: Record<string, unknown> } | undefined)
    ?.job;

  // The customer select is required — gate on the lookup's loading/error
  // states instead of silently rendering an empty select.
  if (jobQuery.isLoading || customersQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (customersQuery.isError) {
    return (
      <EmptyState
        title="Could not load customers"
        description="The customer list failed to load, so the job form can't be edited yet."
        action={{ label: "Try again", onClick: () => customersQuery.refetch() }}
      />
    );
  }

  if (jobQuery.isError || !job) {
    return (
      <EmptyState
        title="Job not found"
        description="This job may have been removed, or you don't have access to it."
        action={{ label: "Back to jobs", onClick: () => navigate("/jobs") }}
      />
    );
  }

  return (
    <EntityForm<JobFormValues>
      title="Edit job"
      breadcrumbs={[{ label: "Jobs", href: "/jobs" }, { label: "Edit" }]}
      defaultValues={jobToFormValues(job)}
      fields={jobFields(customerOptions)}
      submitLabel="Save changes"
      onSubmit={onSubmit}
      cancelHref={`/jobs/${id}`}
      error={error}
      isSubmitting={updateJob.isPending}
    />
  );
}
